import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner, Alert, Table, ProgressBar, Form, Modal, Badge } from "react-bootstrap";
import {
  Calendar,
  Users,
  MapPin,
  Monitor,
  BookOpen,
  Video,
  VideoOff,
  Clock,
  CheckCircle,
  UserCheck,
  AlertCircle,
  XCircle,
  Star,
  ClipboardList,
  Brain,
  Code,
  MessageSquare,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import {
  AppCard,
  AppCardContent,
  AppBadge,
  AppButton,
  StatusBadge,
  PageHeader,
  ConfirmModal,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  UserAvatar,
} from "../ui";
import TrainerTodaySession from "../Syllabus/TrainerTodaySession";
import BatchVideosPage from "../Videos/BatchVideosPage";
import BatchMaterialsPage from "../Materials/BatchMaterialsPage";
import CommonToaster from "../Common/CommonToaster";
import PreMeetingCheck from "../Classroom/PreMeetingCheck";
import "./InstructorBatchDetail.scss";

/* ── Star Rating Component ── */
function StarRating({ value, onChange, disabled, size = 20 }) {
  return (
    <div className="ibd-star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={`ibd-star ${n <= value ? "ibd-star--filled" : "ibd-star--empty"} ${disabled ? "ibd-star--disabled" : ""}`}
          onClick={() => !disabled && onChange(n)}
          fill={n <= value ? "#f59e0b" : "none"}
          stroke={n <= value ? "#f59e0b" : "currentColor"}
        />
      ))}
    </div>
  );
}

/* ── Feedback Modal ── */
function FeedbackModal({ show, onHide, student, batchId, existingReview, onSaved, setToast }) {
  const [performance, setPerformance] = useState(0);
  const [participation, setParticipation] = useState(0);
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && existingReview) {
      setPerformance(existingReview.performance || 0);
      setParticipation(existingReview.participation || 0);
      setRemark(existingReview.remark || "");
    } else if (show) {
      setPerformance(0);
      setParticipation(0);
      setRemark("");
    }
  }, [show, existingReview]);

  const handleSubmit = async () => {
    if (!performance || !participation) {
      setToast({ show: true, message: "Please set both ratings.", type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/student-reviews", {
        batchId,
        studentId: student._id,
        performance,
        participation,
        remark,
      });
      onSaved(student._id, { performance, participation, remark, studentId: student._id });
      setToast({ show: true, message: "Feedback saved!", type: "success" });
      onHide();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to save.", type: "error" });
    }
    setSaving(false);
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: "1rem" }}>
          <MessageSquare size={18} />
          Feedback for {student?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Performance</label>
          <p className="ibd-feedback-hint">How well does the student grasp and apply concepts?</p>
          <StarRating value={performance} onChange={setPerformance} disabled={saving} size={24} />
        </div>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Participation</label>
          <p className="ibd-feedback-hint">How actively does the student engage in class, ask doubts?</p>
          <StarRating value={participation} onChange={setParticipation} disabled={saving} size={24} />
        </div>
        <div className="ibd-feedback-field">
          <label className="ibd-feedback-label">Remark <span className="text-muted fw-normal">(optional)</span></label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={200}
            placeholder="e.g. Strong in DSA, needs more practice in SQL..."
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={saving}
            className="ibd-remark-input"
          />
          <small className="text-muted">{remark.length}/200</small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <AppButton variant="ghost" onClick={onHide} disabled={saving}>Cancel</AppButton>
        <AppButton onClick={handleSubmit} disabled={saving}>
          {saving ? <><Spinner size="sm" animation="border" className="me-1" /> Saving...</> : existingReview ? "Update Feedback" : "Save Feedback"}
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
}

/* ── Instructor Students Review Tab ── */
function InstructorStudentsReview({ batchId, setToast }) {
  const [students, setStudents] = useState([]);
  const [reviews, setReviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedbackStudent, setFeedbackStudent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [studRes, revRes] = await Promise.all([
        apiClient.get(`/student-reviews/batch/${batchId}/students`),
        apiClient.get(`/student-reviews/batch/${batchId}`),
      ]);
      setStudents(studRes.data.students || []);
      const revMap = {};
      for (const r of revRes.data.reviews || []) {
        revMap[r.studentId] = r;
      }
      setReviews(revMap);
    } catch {
      setStudents([]);
    }
    setLoading(false);
  }, [batchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReviewSaved = (studentId, review) => {
    setReviews((prev) => ({ ...prev, [studentId]: review }));
  };

  if (loading) {
    return <div className="text-center py-4"><Spinner size="sm" animation="border" /></div>;
  }

  if (students.length === 0) {
    return <p className="text-muted mt-3" style={{ fontSize: "0.85rem" }}>No active students in this batch.</p>;
  }

  return (
    <>
      <AppCard className="ibd-info-card">
        <AppCardContent>
          <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
            <Users size={18} />
            Students
            <small className="text-muted" style={{ fontWeight: 400, fontSize: "0.75rem", marginLeft: 8 }}>
              ({students.length} active)
            </small>
          </h6>
          <div className="table-responsive">
            <Table size="sm" hover className="mb-0 ibd-review-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th className="text-center">AI Attempts</th>
                  <th className="text-center">Coding Practice</th>
                  <th className="text-center">Assignments</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const review = reviews[s._id];
                  return (
                    <tr key={s._id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <UserAvatar name={s.name} size="sm" />
                          <span className="fw-medium" style={{ fontSize: "0.85rem" }}>{s.name}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Brain size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.aiAttempts || 0}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <Code size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.codingSessions || 0}</span>
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <ClipboardList size={14} className="text-muted" />
                          <span style={{ fontSize: "0.85rem" }}>{s.assignmentsSubmitted || 0}</span>
                        </div>
                      </td>
                      <td className="align-middle text-end">
                        <AppButton
                          size="sm"
                          variant={review ? "ghost" : "outline"}
                          onClick={() => setFeedbackStudent(s)}
                          className="d-inline-flex align-items-center gap-1"
                        >
                          <MessageSquare size={13} />
                          {review ? "Edit" : "Feedback"}
                        </AppButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </AppCardContent>
      </AppCard>

      {feedbackStudent && (
        <FeedbackModal
          show={!!feedbackStudent}
          onHide={() => setFeedbackStudent(null)}
          student={feedbackStudent}
          batchId={batchId}
          existingReview={reviews[feedbackStudent._id]}
          onSaved={handleReviewSaved}
          setToast={setToast}
        />
      )}
    </>
  );
}

/**
 * InstructorBatchDetail
 *
 * Instructor-facing batch detail page accessible at /i/batch/:batchId.
 * Shows batch header info, Start/End class controls, and TrainerTodaySession (syllabus execution widget).
 */
const InstructorBatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // ── Attendance state ──
  const [liveSession, setLiveSession] = useState(null);
  const [attendanceStats, setAttendanceStats] = useState(null);

  useEffect(() => {
    const fetchBatch = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await apiClient.get(`/batches/${batchId}`);
        setBatch(data.batch || data);
      } catch {
        setError("Failed to load batch details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBatch();

    // Refetch when page is restored from bfcache (e.g. returning from classroom)
    const handlePageShow = (e) => {
      if (e.persisted) fetchBatch();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [batchId]);

  // Fetch live attendance when batch is live, poll every 15s
  useEffect(() => {
    if (!batch?.isLive) {
      setLiveSession(null);
      return;
    }
    const fetchLive = async () => {
      try {
        const { data } = await apiClient.get(`/attendance/batch/${batchId}/live`);
        setLiveSession(data.session);
      } catch { /* ignore */ }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 15000);
    return () => clearInterval(interval);
  }, [batchId, batch?.isLive]);

  // Fetch attendance stats once
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await apiClient.get(`/attendance/batch/${batchId}/stats`);
        setAttendanceStats(data);
      } catch { /* ignore */ }
    };
    fetchStats();
  }, [batchId]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: "Asia/Kolkata",
        })
      : "—";

  const formatTime = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  // ── Start / End Meeting ──
  const [showStartConfirm, setShowStartConfirm] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  const handleStartMeeting = () => {
    apiClient
      .post(`/batches/${batchId}/start-meeting`)
      .then(({ data }) => {
        setToast({ show: true, message: "Meeting started successfully.", type: "success" });

        // V2 / platformHosted: full-page nav to embedded classroom; V1: open plain Zoom link
        if (data.zoomMode === 'v2' || data.zoomMode === 'platformHosted') {
          window.location.href = `/i/classroom/${batchId}`;
        } else if (batch.meetingLink) {
          window.open(batch.meetingLink, "_blank");
        }

        setBatch((prev) => ({ ...prev, isLive: true }));
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Failed to start meeting.";
        setToast({ show: true, message: msg, type: "danger" });
      });
  };

  const handleEndMeeting = () => {
    if (!batch) return;
    setShowEndConfirm(false);
    apiClient
      .post(`/batches/${batchId}/end-meeting`)
      .then(() => {
        setToast({ show: true, message: "Meeting ended successfully.", type: "success" });
        setBatch((prev) => ({ ...prev, isLive: false }));
      })
      .catch(() => {
        setToast({ show: true, message: "Failed to end meeting.", type: "danger" });
      });
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-5 text-center">
        {error}
      </Alert>
    );
  }

  if (!batch) {
    return (
      <div className="text-muted text-center my-5">Batch not found.</div>
    );
  }

  return (
    <div className="ibd-page">
      {/* ── Page Header ── */}
      <PageHeader
        backButton
        title={batch.name}
        description={batch.course?.name || "—"}
        actions={
          <div className="ibd-header-actions">
            {(batch.mode === 'Online' || batch.mode === 'Both' || batch.meetingLink || batch.zoomMeetingId) && !batch.isLive && (
              <AppButton
                variant="primary"
                size="sm"
                onClick={() => setShowStartConfirm(true)}
              >
                <Video size={16} />
                Start Class
              </AppButton>
            )}
            {(batch.mode === 'Online' || batch.mode === 'Both' || batch.meetingLink || batch.zoomMeetingId) && batch.isLive && (
              <AppButton
                variant="destructive"
                size="sm"
                onClick={() => setShowEndConfirm(true)}
              >
                <VideoOff size={16} />
                End Class
              </AppButton>
            )}
            {batch.isLive && (
              <AppBadge variant="success" className="ibd-live-badge">
                <span className="ibd-live-dot" />
                LIVE
              </AppBadge>
            )}
          </div>
        }
      />

      {/* ── Batch Info Strip ── */}
      <AppCard className="ibd-info-card">
        <AppCardContent className="ibd-info-strip">
          <div className="ibd-info-item">
            <Calendar size={15} />
            <span>{formatDate(batch.startDate)} – {formatDate(batch.endDate)}</span>
          </div>
          {batch.startTime && batch.endTime && (
            <div className="ibd-info-item">
              <Clock size={15} />
              <span>{formatTime(batch.startTime)} – {formatTime(batch.endTime)}</span>
            </div>
          )}
          <div className="ibd-info-item">
            <Users size={15} />
            <span>{batch.strength ?? "—"} students</span>
          </div>
          <div className="ibd-info-item">
            <Monitor size={15} />
            <span>{batch.mode || "—"}</span>
          </div>
          {batch.room && (
            <div className="ibd-info-item">
              <MapPin size={15} />
              <span>{batch.room}</span>
            </div>
          )}
          <div className="ibd-info-item">
            <StatusBadge status={batch.status} />
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Live Attendance (visible when class is live) ── */}
      {batch.isLive && liveSession && ((
        <AppCard className="ibd-info-card">
          <AppCardContent>
            <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
              <UserCheck size={18} />
              Live Attendance
              <AppBadge variant="success" style={{ marginLeft: 8, fontSize: '0.7rem' }}>
                {(liveSession.participants || []).length} joined
              </AppBadge>
            </h6>
            {(liveSession.participants || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No students have joined yet.</p>
            ) : (
              <div className="ibd-live-participants">
                {(liveSession.participants || []).map((p, idx) => {
                  // If last heartbeat within 60s → active; else stale (might have left)
                  const lastHb = p.lastHeartbeat ? new Date(p.lastHeartbeat) : null;
                  const isActive = lastHb && (Date.now() - lastHb.getTime() < 60000);
                  const hasLeft = !!p.leftAt;
                  return (
                    <div key={idx} className={`ibd-participant-chip ${hasLeft ? 'ibd-chip-left' : isActive ? '' : 'ibd-chip-stale'}`}>
                      {hasLeft
                        ? <XCircle size={14} className="text-danger" />
                        : isActive
                          ? <CheckCircle size={14} className="text-success" />
                          : <AlertCircle size={14} className="text-warning" />
                      }
                      <span>{p.name || 'Student'}</span>
                      <small className="text-muted">
                        {hasLeft
                          ? `left ${new Date(p.leftAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}`
                          : new Date(p.joinedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
                        }
                      </small>
                    </div>
                  );
                })}
              </div>
            )}
          </AppCardContent>
        </AppCard>
      ))}

      {/* ── Tabs: Students, Attendance, Videos, Materials, Syllabus ── */}
      <Tabs defaultValue="students" className="ibd-tabs mt-3">
        <TabsList>
          <TabsTrigger value="students"><ClipboardList size={14} className="me-1" /> Students</TabsTrigger>
          <TabsTrigger value="attendance"><UserCheck size={14} className="me-1" /> Attendance</TabsTrigger>
          <TabsTrigger value="videos"><Video size={14} className="me-1" /> Videos</TabsTrigger>
          <TabsTrigger value="materials"><BookOpen size={14} className="me-1" /> Materials</TabsTrigger>
          <TabsTrigger value="syllabus"><BookOpen size={14} className="me-1" /> Syllabus</TabsTrigger>
        </TabsList>

        {/* ── Students & Reviews ── */}
        <TabsContent value="students">
          <InstructorStudentsReview batchId={batchId} setToast={setToast} />
        </TabsContent>

        {/* ── Attendance Overview ── */}
        <TabsContent value="attendance">
          {attendanceStats && attendanceStats.totalSessions > 0 ? (
            <AppCard className="ibd-info-card">
              <AppCardContent>
                <h6 className="ibd-section-title" style={{ marginBottom: 12 }}>
                  <Users size={18} />
                  Attendance Overview
                  <small className="text-muted" style={{ fontWeight: 400, fontSize: '0.75rem', marginLeft: 8 }}>
                    ({attendanceStats.totalSessions} session{attendanceStats.totalSessions !== 1 ? 's' : ''})
                  </small>
                </h6>
                <Table size="sm" hover responsive className="mb-0" style={{ fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th style={{ width: 90 }}>Present</th>
                      <th style={{ width: 170 }}>Attendance</th>
                      <th style={{ width: 110 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceStats.students.map((s) => {
                      const lastDetail = s.details && s.details.length > 0 ? s.details[s.details.length - 1] : null;
                      return (
                        <tr key={s.studentId}>
                          <td>{s.name}</td>
                          <td>{s.attended} / {s.total}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <ProgressBar
                                now={s.percentage}
                                variant={s.percentage >= 75 ? 'success' : s.percentage >= 50 ? 'warning' : 'danger'}
                                style={{ height: 6, flex: 1 }}
                              />
                              <small style={{ minWidth: 32 }}>{s.percentage}%</small>
                            </div>
                          </td>
                          <td>
                            {lastDetail ? (
                              <span className={`ibd-att-status ibd-att-${lastDetail.status}`}>
                                {lastDetail.status === 'present'
                                  ? <><CheckCircle size={12} /> Present</>
                                  : lastDetail.status === 'partial'
                                    ? <><AlertCircle size={12} /> Partial ({lastDetail.activeMins}m)</>
                                    : lastDetail.status === 'dropped'
                                      ? <><XCircle size={12} /> Dropped</>
                                      : 'Pending'
                                }
                              </span>
                            ) : (
                              <span className="text-muted">Absent</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </AppCardContent>
            </AppCard>
          ) : (
            <p className="text-muted mt-3" style={{ fontSize: '0.85rem' }}>No attendance sessions recorded yet.</p>
          )}
        </TabsContent>

        {/* ── Class Videos ── */}
        <TabsContent value="videos">
          <div className="ibd-syllabus-section">
            <BatchVideosPage batchId={batchId} role="instructor" currentUserId={getCurrentUser()?._id || getCurrentUser()?.id} />
          </div>
        </TabsContent>

        {/* ── Class Materials ── */}
        <TabsContent value="materials">
          <div className="ibd-syllabus-section">
            <BatchMaterialsPage batchId={batchId} courseId={batch?.course?._id || batch?.course} role="instructor" currentUserId={getCurrentUser()?._id || getCurrentUser()?.id} />
          </div>
        </TabsContent>

        {/* ── Syllabus Progress ── */}
        <TabsContent value="syllabus">
          <div className="ibd-syllabus-section">
            <TrainerTodaySession
              batchId={batchId}
              batchTiming={{
                startTime: batch.startTime,
                endTime: batch.endTime,
              }}
            />
          </div>
        </TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PreMeetingCheck
        show={showStartConfirm}
        role="instructor"
        batchName={batch?.name}
        batchId={batchId}
        confirmText="Start Class"
        onReady={() => { setShowStartConfirm(false); handleStartMeeting(); }}
        onCancel={() => setShowStartConfirm(false)}
      />

      <ConfirmModal
        show={showEndConfirm}
        onHide={() => setShowEndConfirm(false)}
        onConfirm={handleEndMeeting}
        title="End Class?"
        message="This will end the meeting for all participants. Are you sure you want to end the class?"
        confirmText="End Class"
        cancelText="Cancel"
        variant="warning"
      />
    </div>
  );
};

export default InstructorBatchDetail;
